/**
 * Visits panel, Step A — the two identity marks and the logo glyph.
 * Copy and rules live in `visit-identity.ts`; see its header.
 *
 * Tooltips use the shared `Tooltip` atom (hover, keyboard focus, tap), never
 * `title=`.
 */

import { Bot, ShieldAlert, ShieldCheck, ShieldX } from 'lucide-react';

import type { AuditRecord } from '@sill/types';
import { inferUaProductToken } from '@sill/types';

import { Tooltip } from '../../components/atoms/index.js';
import { useC } from '../../lib/theme.js';
import { PersonGlyph } from '../people/PersonGlyph.js';
import { ProviderLogo } from './ProviderLogo.js';
import { SillMark } from './SillMark.js';
import { confirmedProof, mismatchReason, visitLogo, visitMark, type IdentityStateResult } from './visit-identity.js';
import { isUcpOrder, isUcpSillBranded } from './ucp.js';

const sansFont = "'Geist', sans-serif";

/**
 * Confirmed (mint ShieldCheck) or Mismatch (coral ShieldX) after the name.
 * Renders nothing for every other state. `withLabel` adds the word, used
 * in the detail header only.
 */
export function VisitIdentityMark({
  record,
  result,
  withLabel = false,
  size = 12,
  note,
  label: labelOverride,
}: {
  record: AuditRecord;
  result: IdentityStateResult;
  withLabel?: boolean;
  size?: number;
  /** Extra sentence after the proof line (Step B: "Confirmed for X of Y visits."). */
  note?: string | null;
  /** Visible label instead of "Confirmed" / "Mismatch" (with `withLabel`). */
  label?: string;
}): JSX.Element | null {
  const C = useC();
  const mark = visitMark(result);
  if (mark === null) return null;
  const confirmed = mark === 'confirmed';
  const baseTip = confirmed ? confirmedProof(result) : mismatchReason(record, result);
  const tip = note !== undefined && note !== null && note.length > 0
    ? (baseTip !== null ? `${baseTip} ${note}` : note)
    : baseTip;
  const color = confirmed ? C.mintFg : C.coralFg;
  const label = labelOverride ?? (confirmed ? 'Confirmed' : 'Mismatch');
  const Icon = confirmed ? ShieldCheck : ShieldX;
  const glyph = (
    <span
      className="inline-flex items-center gap-1"
      style={{ color, fontFamily: sansFont, fontSize: 12, fontWeight: 500, lineHeight: 1 }}
      data-testid={`visit-mark-${mark}`}
    >
      <Icon size={size} color={color} aria-hidden="true" style={{ flexShrink: 0 }} />
      {withLabel ? <span>{label}</span> : null}
    </span>
  );
  if (tip === null) return glyph;
  return (
    <Tooltip content={tip} side="bottom" aria-label={`${label}: ${tip}`}>
      {glyph}
    </Tooltip>
  );
}

/**
 * The glyph before the name. One rule for the list, the header and the
 * Overview feed (see `visitLogo`). Without an explicit `label`, the UA's
 * product token names the monogram tile (ADR-0242), so a bot Sill has no
 * logo for still gets a distinct glyph. A human visit shows its country
 * flag (person glyph when no country; founder 2026-10-13); `flag={false}`
 * keeps the person glyph, for rows that stand for many people.
 */
export function VisitLogoGlyph({
  record,
  result,
  size = 14,
  fallbackColor,
  label,
  flag = true,
}: {
  record: AuditRecord;
  result: IdentityStateResult;
  size?: number;
  fallbackColor?: string;
  label?: string;
  flag?: boolean;
}): JSX.Element {
  const C = useC();
  // ops2026 UCP checkout (2026-10-21) — a UCP order whose platform is
  // Sill's own ops console (staff_test or ops.sill.so) carries the Sill
  // mark, like a Sill-owned brand logo. The host-brand mapping for
  // other UCP platforms (e.g. google.com → Google) goes through
  // AgentLogo's `agent_id` → bundled-brand resolution below; a host
  // we don't bundle a mark for falls back to the generic Bot glyph,
  // and we never invent a logo from a host string.
  if (isUcpOrder(record) && isUcpSillBranded(record)) {
    return <SillMark size={size} />;
  }
  const logo = visitLogo(record, result);
  if (logo.kind === 'rejected') {
    return <ShieldAlert size={size} color={C.coralFg} className="flex-shrink-0" aria-hidden="true" />;
  }
  if (logo.kind === 'mismatch') {
    return <Bot size={size} color={C.coralFg} style={{ flexShrink: 0 }} aria-label="Unknown sender" />;
  }
  if (logo.kind === 'sill') {
    return <SillMark size={size} />;
  }
  const visitorClass = record.identification_match?.visitor_class;
  if (visitorClass === 'human_likely' && logo.agent_id === null) {
    return <PersonGlyph country={flag ? record.geo_country : null} size={size} color={fallbackColor ?? C.muted} />;
  }
  const monogramLabel = label ?? (visitorClass === 'human_likely' ? undefined : uaLabel(record.user_agent));
  return (
    <ProviderLogo
      agent_id={logo.agent_id}
      {...(visitorClass !== undefined ? { visitor_class: visitorClass } : {})}
      size={size}
      fallbackColor={fallbackColor ?? (visitorClass === 'matched_agent' ? C.limeFg : C.muted)}
      {...(monogramLabel !== undefined ? { label: monogramLabel } : {})}
    />
  );
}

/** Visitor-supplied UA → a short product token for the monogram, bounded. */
function uaLabel(ua: string | undefined): string | undefined {
  if (ua === undefined || ua.length === 0) return undefined;
  const t = inferUaProductToken(ua.slice(0, 512));
  return t !== null && t.length > 0 ? t.slice(0, 64) : undefined;
}
